import React, { useState, useEffect } from 'react';
import { BookOpen, Clock, Play, Info, X, History, Calendar, Award, TrendingUp, LogOut, User, GraduationCap, Brain, Trophy } from 'lucide-react';

const ChapterExamSelector = ({ onStartExam, user, onLogout }) => {
  const [selectedExamType, setSelectedExamType] = useState('certification');
  const [selectedSet, setSelectedSet] = useState('');
  const [selectedTestNumber, setSelectedTestNumber] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [examHistory, setExamHistory] = useState([]);
  const [averageScores, setAverageScores] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isPracticeMode, setIsPracticeMode] = useState(true);
  const [sets, setSets] = useState([]);
  const [certificationTests, setCertificationTests] = useState([]);
  const [isLoadingSets, setIsLoadingSets] = useState(true);
  const [focusedHistory, setFocusedHistory] = useState([]);
  
  const isTrialUser = user?.subscriptionType === 'trial';
  const API_BASE = process.env.REACT_APP_API_ENDPOINT;
  const QUESTIONS_PER_CERT_TEST = 56;
  const ENV_TOTAL_QUESTIONS = parseInt(process.env.REACT_APP_TOTAL_QUESTIONS || '', 10);

  const ENABLE_FOCUSED_EXAM = false;


  // ✅ Force trial users to Mixed Practice
  useEffect(() => {
    if (isTrialUser) {
      setSelectedExamType('mixed');
    }
  }, [isTrialUser]);

  // ✅ Fetch dynamic Sets from getQuestionSetSummary
  useEffect(() => {
    const fetchSets = async () => {
      setIsLoadingSets(true);
      try {
        const token = localStorage.getItem('authToken') || user?.token || '';
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        console.log('🔹 Fetching sets from:', `${API_BASE}/getQuestionSetSummary`);
        const res = await fetch(`${API_BASE}/getQuestionSetSummary`, { method: 'GET', headers });
        //const res = await fetch(`${API_BASE}/go-get-question-set-summary-lambda`, { method: 'GET', headers });
        
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        
        const responseData = await res.json();
        console.log('📦 Raw response:', responseData);
        
        // Handle Lambda response format (body might be stringified JSON)
        const data = typeof responseData.body === 'string' 
          ? JSON.parse(responseData.body) 
          : responseData;

        console.log('✅ Parsed data:', data);

        if (data.setCounts && Object.keys(data.setCounts).length > 0) {
          let fetchedSets = Object.entries(data.setCounts).map(([id, count]) => ({
            id,
            name: id,
            questions: `${count} questions`,
            time: 'Dynamic',
          }));

          // Sort sets naturally (Set 1_1, Set 1_2, etc.)
          fetchedSets.sort((a, b) => {
            // Extract numbers for proper sorting
            const aMatch = a.id.match(/Set (\d+)(?:_(\d+))?/);
            const bMatch = b.id.match(/Set (\d+)(?:_(\d+))?/);
            
            if (aMatch && bMatch) {
              const aSet = parseInt(aMatch[1]);
              const bSet = parseInt(bMatch[1]);
              if (aSet !== bSet) return aSet - bSet;
              
              const aPart = parseInt(aMatch[2] || '0');
              const bPart = parseInt(bMatch[2] || '0');
              return aPart - bPart;
            }
            
            return a.id.localeCompare(b.id);
          });

          console.log('✅ Processed sets:', fetchedSets);
          setSets(fetchedSets);
        } else {
          console.warn('⚠️ No setCounts found in response');
          setSets([]);
        }
      } catch (err) {
        console.error('❌ Failed to fetch sets:', err);
        setSets([]);
      } finally {
        setIsLoadingSets(false);
      }
    };
    
    if (API_BASE) {
      fetchSets();
    }
  }, [API_BASE, user]);

  // ✅ Fetch certification tests count
  useEffect(() => {
    const buildTests = (n) => Array.from({ length: n }, (_, i) => i + 1);

    const controller = new AbortController();
    const signal = controller.signal;
    let mounted = true;

    (async () => {
      try {
        const token = localStorage.getItem('authToken') || user?.token || '';
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/getQuestionSetSummary`, {
  method: 'GET',
  headers,
  signal,
});

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        
        const responseData = await res.json();
        const data = typeof responseData.body === 'string' 
          ? JSON.parse(responseData.body) 
          : responseData;

        console.log('📊 Certification tests data:', data);

        if (data.totalQuestions) {
          const totalTests = Math.floor(data.totalQuestions / QUESTIONS_PER_CERT_TEST);
          console.log(`✅ Total certification tests available: ${totalTests}`);
          if (mounted) setCertificationTests(buildTests(totalTests));
        } else if (ENV_TOTAL_QUESTIONS) {
          // Fallback to env variable
          const totalTests = Math.floor(ENV_TOTAL_QUESTIONS / QUESTIONS_PER_CERT_TEST);
          console.log(`⚠️ Using fallback ENV total tests: ${totalTests}`);
          if (mounted) setCertificationTests(buildTests(totalTests));
        } else {
          console.warn('⚠️ No totalQuestions found in Lambda response or ENV.');
          if (mounted) setCertificationTests([]);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('❌ Error fetching certification test count:', err);
        
        // Fallback to env variable on error
        if (ENV_TOTAL_QUESTIONS && mounted) {
          const totalTests = Math.floor(ENV_TOTAL_QUESTIONS / QUESTIONS_PER_CERT_TEST);
          setCertificationTests(buildTests(totalTests));
        } else if (mounted) {
          setCertificationTests([]);
        }
      }
    })();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [user, API_BASE, ENV_TOTAL_QUESTIONS, QUESTIONS_PER_CERT_TEST]);

  // ✅ Auto-select first test when certification mode is activated
  useEffect(() => {
    if (selectedExamType === 'certification' && certificationTests.length > 0) {
      setSelectedTestNumber(prev => prev || String(certificationTests[0]));
    }
  }, [selectedExamType, certificationTests]);

  // ✅ Load exam history when component mounts
  useEffect(() => {
    if (showHistory) {
      loadExamHistory();
    }
  }, [showHistory]);

  const loadExamHistory = async () => {
  setIsLoadingHistory(true);
  try {
    const token = localStorage.getItem('authToken') || user?.token;
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_BASE}/java-cert-get-exam-history?limit=20`, {
      //const response = await fetch(`${API_BASE}/go-get-exam-history-lambda?limit=20`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        onLogout();
        return;
      }
      throw new Error(`Failed to fetch exam history: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.success && data.examHistory) {
      // ✅ Separate focused exams from main history
      const mainHistory = data.examHistory.filter(exam => 
        exam.examType !== 'focused'
      );
      
      const focusedExams = data.examHistory.filter(exam => 
        exam.examType === 'focused'
      );
      
      console.log('📊 History breakdown:', {
        total: data.examHistory.length,
        mainHistory: mainHistory.length,
        focusedExams: focusedExams.length
      });
      
      setExamHistory(mainHistory);
      setFocusedHistory(focusedExams);
      setAverageScores(data.summary ? JSON.parse(JSON.stringify(data.summary)) : (data.averageScores || null));
    } else {
      console.error('Invalid response format:', data);
      setExamHistory([]);
      setFocusedHistory([]);
      setAverageScores(null);
    }
    
  } catch (error) {
    console.error('Failed to load exam history:', error);
    setExamHistory([]);
    setFocusedHistory([]);
    setAverageScores(null);
  } finally {
    setIsLoadingHistory(false);
  }
};





const handleStartExam = () => {
  // Validation for certification tests
  if (selectedExamType === 'certification' && !selectedTestNumber) {
    alert('Please select a test number for certification exam');
    return;
  }

  if (!ENABLE_FOCUSED_EXAM && selectedExamType === 'focused') {
  alert('Focused exams are temporarily unavailable.');
  return;
}
  // Validation for focused exams
  if (selectedExamType === 'focused' && !selectedSet) {
    alert('Please select a set for focused exam');
    return;
  }

  // ✨ Calculate dynamic time limit
  let timeLimit;
  
  if (selectedExamType === 'certification') {
    timeLimit = 120; // 56 questions = 120 minutes
  } else if (selectedExamType === 'full') {
    timeLimit = 120; // 56 questions = 120 minutes
  } else if (selectedExamType === 'mixed') {
    timeLimit = isTrialUser ? 60 : 100; // 30 or 50 questions
  } else if (selectedExamType === 'focused') {
    // Dynamic time based on question count
    const selectedSetData = sets.find(s => s.id === selectedSet);
    if (selectedSetData) {
      const questionCount = parseInt(selectedSetData.questions.match(/\d+/)[0]);
      
      // Different time allowance for practice vs exam mode
      if (isPracticeMode) {
        timeLimit = Math.ceil(questionCount * 3); // 3 min per question
      } else {
        timeLimit = Math.ceil(questionCount * 2); // 2 min per question
      }
    } else {
      timeLimit = 60; // Fallback
    }
  } else {
    timeLimit = 120; // Default
  }

  const examConfig = {
    type: selectedExamType,
    chapter: selectedSet,
    testNumber: selectedTestNumber,
    timeLimit: timeLimit,
    isPracticeMode: isPracticeMode
  };
  
  if (isTrialUser && selectedExamType === 'mixed') {
    examConfig.limit = 30;
  }
  
  console.log('🚀 Starting exam with config:', examConfig);
  onStartExam(examConfig);
};

  return (
    <div style={{
      maxWidth: '900px',
      margin: '0 auto',
      padding: '10px 16px'
    }}>
      {/* ✅ MOBILE RESPONSIVE CSS - Only applies on small screens */}
      <style>{`
        @media (max-width: 768px) {
          .exam-type-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px !important;
          }
          .study-mode-grid {
            flex-direction: column !important;
          }
          .focused-set-grid {
            grid-template-columns: 1fr !important;
          }
          .header-user-section {
            position: relative !important;
            top: unset !important;
            right: unset !important;
            justify-content: center !important;
            margin-bottom: 16px !important;
            flex-wrap: wrap !important;
          }
          .start-button-group {
            flex-direction: column !important;
            width: 100% !important;
          }
          .start-button-group button {
            width: 100% !important;
          }
          .history-header {
            flex-direction: column !important;
            gap: 12px !important;
            align-items: flex-start !important;
          }
          .card-padding-large {
            padding: 16px !important;
          }
          .trial-badge {
            font-size: 11px !important;
            white-space: normal !important;
          }
        }
        
        @media (max-width: 480px) {
          .exam-type-grid {
            grid-template-columns: 1fr !important;
          }
          .exam-type-card {
            padding: 16px !important;
          }
        }
      `}</style>

      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        textAlign: 'center',
        position: 'relative',
      }}>
        {/* User Info + Logout */}
        <div className="header-user-section" style={{
          position: 'absolute',
          top: '14px',
          right: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
         {user && (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    //color: '#1f2937',
    padding: '8px 12px',
    backgroundColor: '#ffffff',
    border: '2px solid #667eea',
    borderRadius: '8px',
    fontSize: '14px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
  }}>
    <User size={16} style={{ color: '#667eea', flexShrink: 0 }} />
    <span
  style={{
    cssText: 'color: #000 !important; font-weight: 600; white-space: nowrap; text-shadow: none; mix-blend-mode: normal;'
  }}
>
  {user.name || user.email}
</span>

  </div>
)}

          <button 
            onClick={onLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              border: '2px solid #ef4444',
              backgroundColor: 'white',
              color: '#ef4444',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#ef4444';
              e.currentTarget.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.color = '#ef4444';
            }}
          >
            <LogOut size={15} />
            Logout
          </button>
        </div>

        {/* Title */}
        <BookOpen style={{
          color: '#667eea',
          margin: '8px auto 10px auto',
          display: 'block',
        }} size={48} />
        <h1 style={{ fontSize: '22px', fontWeight: '700', margin: '0 0 4px 0' }}>
          Java Certification Practice
        </h1>
        <p style={{ color: '#64748b', fontSize: '15px', margin: '0' }}>
          Java OCA 8 (1Z0-808) exam preparation platform
        </p>
      </div>

      {/* Practice/Exam Mode Toggle */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '16px 20px',
        marginBottom: '16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>
          Please select a study mode
        </h2>
        
        <div className="study-mode-grid" style={{ display: 'flex', gap: '16px' }}>
          {/* Practice Mode */}
          <button
            onClick={() => setIsPracticeMode(true)}
            style={{
              flex: 1,
              padding: '20px',
              borderRadius: '12px',
              border: `2px solid ${isPracticeMode ? '#f59e0b' : '#e2e8f0'}`,
              backgroundColor: isPracticeMode ? '#ffffffff' : 'white',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <Brain style={{ marginRight: '8px', color: '#10b981' }} size={24} />
              <strong style={{ color: isPracticeMode ? '#10b981' : '#1e293b' }}>Practice Mode</strong>
            </div>
            <p style={{ margin: '0', color: '#64748b', fontSize: '14px' }}>
              ✓ Show explanations immediately<br/>
              ✓ Learn as you practice<br/>
              ✓ No time pressure
            </p>
          </button>

          {/* Exam Mode */}
          <button
            onClick={() => setIsPracticeMode(false)}
            style={{
              flex: 1,
              padding: '20px',
              borderRadius: '12px',
              border: `2px solid ${!isPracticeMode ? '#f59e0b' : '#e2e8f0'}`,
              backgroundColor: !isPracticeMode ? '#ffffffff' : 'white',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <GraduationCap style={{ marginRight: '8px', color: '#23db7bff' }} size={24} />
              <strong style={{ color: !isPracticeMode ? '#0e0e0eff' : '#1e293b' }}>Exam Mode</strong>
            </div>
            <p style={{ margin: '0', color: '#64748b', fontSize: '14px' }}>
              ✓ Real exam conditions<br/>
              ✓ No explanations during exam<br/>
              ✓ Timed experience
            </p>
          </button>
        </div>
      </div>

      {/* Exam Type Selection */}
      <div className="card-padding-large" style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px 28px',
        marginBottom: '24px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
      }}>
        {/* Section Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            margin: 0,
            color: '#1e293b',
          }}>
            Select Exam Type
          </h2>

          {isTrialUser && (
            <div className="trial-badge" style={{
              backgroundColor: '#ecfdf5',
              border: '1px solid #10b981',
              borderRadius: '8px',
              padding: '6px 12px',
              color: '#065f46',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}>
              🎁 Free Trial Active — only <span style={{ color: '#059669' }}>Mixed Practice</span> available (30 questions, 3 days)
            </div>
          )}
        </div>

        <div className="exam-type-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          {/* Certification Test */}
          <button
            onClick={() => {
              if (!isTrialUser) {
                setSelectedExamType('certification');
                setSelectedSet('');
              }
            }}
            disabled={isTrialUser}
            className="exam-type-card"
            style={{
              padding: '24px',
              borderRadius: '12px',
              border: `2px solid ${selectedExamType === 'certification' ? '#f59e0b' : '#e2e8f0'}`,
              backgroundColor: selectedExamType === 'certification' ? '#ffffffff' : 'white',
              cursor: isTrialUser ? 'not-allowed' : 'pointer',
              opacity: isTrialUser ? 0.4 : 1,
              textAlign: 'left',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <Trophy style={{ marginRight: '8px', color: '#f59e0b' }} size={20} />
              <strong>Certification Test</strong>
            </div>
            <p style={{ margin: '0', color: '#64748b', fontSize: '14px' }}>
              56 questions • 120 minutes<br />
              Fixed test sets (1-{certificationTests.length || '...'})
            </p>
            {isTrialUser && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '8px' }}>Not available in trial</p>}
          </button>

          {/* Random Practice */}
          <button
            onClick={() => {
              if (!isTrialUser) {
                setSelectedExamType('full');
                setSelectedSet('');
                setSelectedTestNumber('');
              }
            }}
            disabled={isTrialUser}
            className="exam-type-card"
            style={{
              padding: '24px',
              borderRadius: '12px',
              border: `2px solid ${selectedExamType === 'full' ? '#f59e0b' : '#e2e8f0'}`,
              backgroundColor: selectedExamType === 'full' ? '#ffffffff' : 'white',
              cursor: isTrialUser ? 'not-allowed' : 'pointer',
              textAlign: 'left',
              transition: 'all 0.3s ease',
              opacity: isTrialUser ? 0.4 : 1,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <Clock style={{ marginRight: '8px', color: '#667eea' }} size={20} />
              <strong>Random Practice</strong>
            </div>
            <p style={{ margin: '0', color: '#64748b', fontSize: '14px' }}>
              56 questions • 120 minutes<br />
              Random selection
            </p>
            {isTrialUser && (
              <p style={{
                color: '#ef4444',
                fontSize: '12px',
                marginTop: '8px',
                fontWeight: '500',
              }}>
                Not available in trial
              </p>
            )}
          </button>

          {/* Mixed Practice */}
          <button
            onClick={() => {
              setSelectedExamType('mixed');
              setSelectedSet('');
              setSelectedTestNumber('');
            }}
            className="exam-type-card"
            style={{
              padding: '24px',
              borderRadius: '12px',
              border: `2px solid ${selectedExamType === 'mixed' ? '#f59e0b' : '#e2e8f0'}`,
              backgroundColor: selectedExamType === 'mixed' ? '#ffffffff' : 'white',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.3s ease',
              boxShadow: isTrialUser ? '0 0 0 3px rgba(139,92,246,0.2)' : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <TrendingUp style={{ marginRight: '8px', color: '#8b5cf6' }} size={20} />
              <strong>Mixed Practice</strong>
            </div>

            <p style={{ margin: '0', color: '#64748b', fontSize: '14px' }}>
              {isTrialUser
                ? <>30 questions • Dynamic time<br/>Trial mode</>
                : <>50 questions • Dynamic time<br/>All chapters combined</>}
            </p>

            {isTrialUser && (
              <p style={{
                color: '#10b981',
                fontSize: '12px',
                marginTop: '8px',
                fontWeight: '500',
              }}>
                🎁 Trial: Limited to 30 questions
              </p>
            )}
          </button>

          {/* Focused Exam */}
          {ENABLE_FOCUSED_EXAM && (
          <button
            onClick={() => {
              if (!isTrialUser) {
                setSelectedExamType('focused');
                setSelectedTestNumber('');
              }
            }}
            disabled={isTrialUser}
            className="exam-type-card"
            style={{
              padding: '24px',
              borderRadius: '12px',
              border: `2px solid ${selectedExamType === 'focused' ? '#10b981' : '#e2e8f0'}`,
              backgroundColor: selectedExamType === 'focused' ? '#f0fdf4' : 'white',
              cursor: isTrialUser ? 'not-allowed' : 'pointer',
              textAlign: 'left',
              transition: 'all 0.3s ease',
              opacity: isTrialUser ? 0.4 : 1,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <BookOpen style={{ marginRight: '8px', color: '#10b981' }} size={20} />
              <strong>Focused Exam</strong>
            </div>

            <p style={{ margin: '0', color: '#64748b', fontSize: '14px' }}>
              Up to 56 questions<br />
              Single set study
            </p>

            {isTrialUser && (
              <p style={{
                color: '#ef4444',
                fontSize: '12px',
                marginTop: '8px',
                fontWeight: '500',
              }}>
                Not available in trial
              </p>
            )}
          </button>
          )}
        </div>

        {/* Certification Test Selector */}
        {selectedExamType === 'certification' && (
          <div style={{
            marginTop: '24px',
            padding: '20px',
            backgroundColor: '#ffffffff',
            borderRadius: '12px',
            border: '2px solid #e0dfddff'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: '0 0 16px 0', color: '#080808ff' }}>
              Select Certification Test
            </h3>
            <p style={{ margin: '0 0 16px 0', color: '#040404ff', fontSize: '14px' }}>
              Each test contains a fixed set of {QUESTIONS_PER_CERT_TEST} questions.
              Choose a test number (1{certificationTests.length ? `-${certificationTests.length}` : ''}):
            </p>

            <select
              value={selectedTestNumber}
              onChange={(e) => setSelectedTestNumber(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #f59e0b',
                fontSize: '16px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="">
                {certificationTests.length === 0 ? 'Loading tests...' : '-- Select a Test Number --'}
              </option>

              {certificationTests.map(testNum => (
                <option key={testNum} value={String(testNum)}>
                  Certification Test #{testNum}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Focused Set Selector */}
        {ENABLE_FOCUSED_EXAM && selectedExamType === 'focused' && (
          <div style={{ marginTop: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: '0 0 16px 0' }}>
              Select Focused Set
            </h3>
            
            {isLoadingSets ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                Loading sets...
              </div>
            ) : sets.length > 0 ? (
              <div className="focused-set-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {sets.map(set => (
                  <button
                    key={set.id}
                    onClick={() => setSelectedSet(set.id)}
                    style={{
                      padding: '16px',
                      borderRadius: '8px',
                      border: `2px solid ${selectedSet === set.id ? '#10b981' : '#e2e8f0'}`,
                      backgroundColor: selectedSet === set.id ? '#f0fdf4' : 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '4px', color: selectedSet === set.id ? '#10b981' : '#1e293b' }}>
                      {set.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                      {set.questions} • {set.time}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                <Info size={48} style={{ margin: '0 auto 16px auto', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: '16px' }}>No sets available</p>
                <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                  Please check your API configuration or try again later
                </p>
              </div>
            )}
          </div>
        )}

        {/* Start Button */}
        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <div className="start-button-group" style={{ display: 'flex', gap: '16px', justifyContent: 'center', alignItems: 'center' }}>
            <button
              onClick={handleStartExam}
              disabled={
                (selectedExamType === 'focused' && !selectedSet) ||
                (selectedExamType === 'certification' && !selectedTestNumber)
              }
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '16px 32px',
                fontSize: '18px',
                fontWeight: '600',
                cursor:
                  (selectedExamType === 'focused' && !selectedSet) ||
                  (selectedExamType === 'certification' && !selectedTestNumber)
                    ? 'not-allowed'
                    : 'pointer',
                opacity:
                  (selectedExamType === 'focused' && !selectedSet) ||
                  (selectedExamType === 'certification' && !selectedTestNumber)
                    ? 0.5
                    : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Play size={20} />
              {selectedExamType === 'certification'
                ? `Start Test #${selectedTestNumber || '...'}`
                : selectedExamType === 'full'
                ? 'Start Random Practice'
                : selectedExamType === 'mixed'
                ? 'Start Mixed Practice'
                : selectedExamType === 'focused'
                ? `Start Focused Exam${selectedSet ? `: ${selectedSet}` : ''}`
                : 'Start Exam'}
            </button>

            {/* History Button */}
            <button
              onClick={() => {
                if (!isTrialUser) setShowHistory(true);
              }}
              disabled={isTrialUser}
              title={
                isTrialUser
                  ? 'History is available only for subscribed users'
                  : 'View your previous exam attempts'
              }
              style={{
                background: 'transparent',
                color: isTrialUser ? '#94a3b8' : '#667eea',
                border: `2px solid ${isTrialUser ? '#cbd5e1' : '#667eea'}`,
                borderRadius: '12px',
                padding: '16px 24px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: isTrialUser ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isTrialUser ? 0.6 : 1,
                transition: 'opacity 0.3s ease',
              }}
            >
              <History size={20} />
              History
            </button>
          </div>
        </div>
      </div>

      {/* History Section */}
      {showHistory && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          marginTop: '24px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
        }}>
          <div className="history-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <h3 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              margin: 0,
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <History size={28} color="#10b981" />
              Last 5 Attempts History
            </h3>
            <button
              onClick={() => setShowHistory(false)}
              style={{
                background: 'none',
                border: '2px solid #e2e8f0',
                color: '#64748b',
                borderRadius: '8px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              Close
            </button>
          </div>

          {/* Average Scores Summary */}
          {averageScores && (
            <div style={{
              marginBottom: '24px',
              padding: '24px',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
            }}>
              <div>
  <h4 style={{
    fontSize: '18px',
    fontWeight: 'bold',
    margin: '0 0 4px 0',
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  }}>
    <TrendingUp size={20} color="#10b981" />
    Average Performance Summary
  </h4>
  <p style={{
    fontSize: '13px',
    color: '#64748b',
    margin: '0 0 16px 0',
    fontStyle: 'italic'
  }}>
    Only Certification Tests (1-25) count toward certificates
  </p>
</div>

                <div style={{
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '16px',
}}>
  {[
    { key: 'certification', label: 'Certification Tests', color: '#f59e0b', icon: Trophy },
    { key: 'full', label: 'Random Practice', color: '#667eea', icon: Award },
    { key: 'mixed', label: 'Mixed Practice', color: '#8b5cf6', icon: TrendingUp },
  ].map(({ key, label, color, icon: Icon }) => (
                  <div
                    key={key}
                    style={{
                      padding: '16px',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      textAlign: 'center',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                    }}>
                      <Icon size={18} color={color} />
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#475569',
                      }}>
                        {label}
                      </span>
                    </div>

                    <div style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color,
                      marginBottom: '4px',
                    }}>
                      {averageScores?.[key]?.avg != null
                        ? `${averageScores[key].avg}%`
                        : 'N/A'}
                    </div>

                    <div style={{
                      fontSize: '12px',
                      color: '#64748b',
                    }}>
                      {averageScores?.[key]?.attempts || 0} attempt
                      {averageScores?.[key]?.attempts !== 1 ? 's' : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isLoadingHistory ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              Loading history...
            </div>
          ) : examHistory.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '16px'
            }}>
              {examHistory
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .slice(0, 5)
                .map((exam, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                      border: '1px solid #e2e8f0',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.05)';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {exam.examType === 'full' && <Award size={16} color="#667eea" />}
                        {exam.examType === 'mixed' && <TrendingUp size={16} color="#8b5cf6" />}
                        {exam.examType === 'focused' && <BookOpen size={16} color="#10b981" />}
                        {exam.examType === 'certification' && <Trophy size={16} color="#f59e0b" />}
                        <strong style={{ color: '#1e293b' }}>{exam.examName}</strong>
                      </div>

                      <span style={{
                        backgroundColor: exam.passed ? '#dcfce7' : '#fee2e2',
                        color: exam.passed ? '#166534' : '#991b1b',
                        fontSize: '12px',
                        fontWeight: '600',
                        borderRadius: '8px',
                        padding: '4px 8px'
                      }}>
                        {exam.passed ? 'PASS' : 'FAIL'}
                      </span>
                    </div>

                    <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
                      <div>Score: <strong>{exam.score}</strong> / {exam.totalQuestions}</div>
                      <div>
                        Percentage:{' '}
                        <span style={{
                          color: exam.percentage >= 65 ? '#10b981' : '#ef4444',
                          fontWeight: '600'
                        }}>
                          {exam.percentage}%
                        </span>
                      </div>
                      <div>Attempt: #{exam.attempt}</div>
                      <div>Date: {new Date(exam.date).toLocaleDateString()}</div>
                      <div>Time: {exam.timeSpent}</div>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#64748b'
            }}>
              <History size={48} style={{ margin: '0 auto 16px auto', opacity: 0.5 }} />
              <p style={{ margin: 0, fontSize: '16px' }}>No exam history found</p>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                Take your first exam to see results here
              </p>
            </div>
          )}
        </div>
      )}

{/* Practice Sessions Section - Focused Exams */}

{ENABLE_FOCUSED_EXAM && showHistory && focusedHistory.length > 0 && (

  <div style={{
    backgroundColor: '#f0fdf4',
    borderRadius: '16px',
    padding: '28px',
    marginTop: '32px',
    border: '2px solid #10b981',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)'
  }}>

    <div style={{
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '20px',
  paddingBottom: '16px',
  borderBottom: '2px solid #d1fae5'
}}>
  <h4 style={{
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
    color: '#065f46',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  }}>
    <BookOpen size={24} color="#10b981" />
    Practice Sessions (Focused Exams)
  </h4>
  
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
    <div style={{
      backgroundColor: '#dcfce7',
      padding: '6px 14px',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: '600',
      color: '#065f46'
    }}>
      {focusedHistory.length} session{focusedHistory.length !== 1 ? 's' : ''}
    </div>
    
    {/* ✅ Close Button */}
    <button
      onClick={() => setShowHistory(false)}
      style={{
        background: 'white',
        border: '2px solid #10b981',
        color: '#065f46',
        borderRadius: '8px',
        padding: '6px 14px',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: '600',
        transition: 'all 0.2s ease'
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = '#10b981';
        e.currentTarget.style.color = 'white';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = 'white';
        e.currentTarget.style.color = '#065f46';
      }}
    >
      Close
    </button>
  </div>
</div>
    
    <div style={{
      padding: '12px 16px',
      backgroundColor: '#dcfce7',
      borderRadius: '10px',
      marginBottom: '24px',
      border: '1px solid #86efac'
    }}>
      <p style={{
        fontSize: '13px',
        color: '#047857',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        lineHeight: '1.5'
      }}>
        <Info size={16} />
        <span>
         <strong>Note:</strong> These practice sessions are for learning and do not count toward 
certificate requirements. Only numbered Certification Tests (1-25) count toward certificates.
        </span>
      </p>
    </div>
    
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '16px'
    }}>
      {focusedHistory
        .sort((a, b) => new Date(b.submittedAt || b.date) - new Date(a.submittedAt || a.date))
        .slice(0, 10)
        .map((exam, index) => (
          <div
            key={index}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '2px solid #10b981',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.15)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                flex: 1
              }}>
                <BookOpen size={18} color="#10b981" />
                <strong style={{ 
                  color: '#1e293b',
                  fontSize: '16px'
                }}>
                  {exam.chapter || exam.examName || 'Practice Session'}
                </strong>
              </div>

              <span style={{
                backgroundColor: exam.passed || (exam.percentage >= 65) ? '#dcfce7' : '#fee2e2',
                color: exam.passed || (exam.percentage >= 65) ? '#166534' : '#991b1b',
                fontSize: '11px',
                fontWeight: '700',
                borderRadius: '6px',
                padding: '4px 10px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {exam.passed || (exam.percentage >= 65) ? 'PASS' : 'FAIL'}
              </span>
            </div>

            <div style={{ 
              fontSize: '14px', 
              color: '#475569', 
              lineHeight: '1.8'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                marginBottom: '4px'
              }}>
                <span>Score:</span>
                <strong style={{ color: '#1e293b' }}>
                  {exam.score} / {exam.totalQuestions}
                </strong>
              </div>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                marginBottom: '4px'
              }}>
                <span>Percentage:</span>
                <span style={{
                  color: (exam.percentage >= 65) ? '#10b981' : '#ef4444',
                  fontWeight: '700',
                  fontSize: '15px'
                }}>
                  {exam.percentage}%
                </span>
              </div>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                marginBottom: '4px',
                paddingTop: '8px',
                borderTop: '1px solid #e2e8f0'
              }}>
                <span style={{ fontSize: '13px' }}>Date:</span>
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                  {new Date(exam.submittedAt || exam.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: '13px' }}>Time Spent:</span>
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                  {exam.timeSpent ? 
                    (typeof exam.timeSpent === 'string' ? exam.timeSpent : `${exam.timeSpent} min`) 
                    : 'N/A'
                  }
                </span>
              </div>
            </div>
          </div>
        ))}
    </div>
    
    {focusedHistory.length > 10 && (
      <div style={{
        textAlign: 'center',
        marginTop: '20px',
        padding: '12px',
        backgroundColor: '#ecfdf5',
        borderRadius: '8px'
      }}>
        <p style={{
          margin: 0,
          fontSize: '13px',
          color: '#047857',
          fontStyle: 'italic'
        }}>
          Showing 10 most recent practice sessions • Total: {focusedHistory.length} sessions
        </p>
      </div>
    )}
  </div>
)}
    </div>

    
  );
};

export default ChapterExamSelector;